import {
  Plan,
  PlanStack,
  PlanType,
  TrialType,
  UpgradeReason,
} from '@zapier/upsells/src/types';

import { canUpgradeForReason } from '@zapier/upsells/src/utils';

type Props = {
  activeTrialType?: TrialType;
  currentPlan: Plan;
  planStacks: PlanStack[];
  upgradeReason: UpgradeReason;
};

// -1 means unlimited
const toInfinity = (value: number) =>
  value === -1 ? Number.MAX_SAFE_INTEGER : value;

const getPlanTypeWhere = (
  func: (plan: Plan) => boolean,
  planStacks: PlanStack[]
) => planStacks.find(planStack => planStack.plans.find(func))?.planType;

export const getNextPlanTypeForUpgradeReason = ({
  activeTrialType,
  currentPlan,
  planStacks,
  upgradeReason,
}: Props): PlanType | undefined => {
  let planType;

  if (planStacks.length === 0) {
    console.error('Property `planStacks` cannot be empty.');
    return undefined;
  }

  const cannotUpgradeForReason = !canUpgradeForReason({
    plan: currentPlan,
    upgradeReason,
  });

  switch (upgradeReason) {
    case 'email-support': {
      planType = 'Starter';
      break;
    }

    case 'live-zaps': {
      planType = cannotUpgradeForReason
        ? currentPlan.type
        : getPlanTypeWhere(
            plan => toInfinity(plan.zapLimit) > currentPlan.zapLimit,
            planStacks
          );

      break;
    }

    case 'multistep': {
      planType = cannotUpgradeForReason
        ? currentPlan.type
        : getPlanTypeWhere(plan => plan.hasMultiStepZaps, planStacks);

      break;
    }

    case 'paths': {
      planType = cannotUpgradeForReason
        ? currentPlan.type
        : getPlanTypeWhere(plan => plan.hasPaths, planStacks);

      break;
    }

    case 'premium': {
      planType = cannotUpgradeForReason
        ? currentPlan.type
        : getPlanTypeWhere(plan => plan.hasPremiumApps, planStacks);

      break;
    }

    case 'premium-limit': {
      planType = cannotUpgradeForReason
        ? currentPlan.type
        : getPlanTypeWhere(
            plan => plan.hasPremiumApps && plan.premiumAppsLimit === -1,
            planStacks
          );

      break;
    }

    case 'tasks':
    case 'tasks-low': {
      /**
       * This is just the _minimum_ plan type that the user could end up on.
       * The `nextPlan` query will select a higher plan type if necessary.
       *
       * As for trials, if the user is on a trial and runs out of tasks, we want
       * to upgrade them to the plan type they're trialing.
       */
      planType = activeTrialType ? activeTrialType : currentPlan.type;
      break;
    }
  }

  if (planType) {
    return planType as PlanType;
  }

  console.error(
    `Unable to find plan type for upgrade reason \`${upgradeReason}\`.`
  );

  return undefined;
};
